import { TeamMemberInvite } from '@/@account/types';
import { REPORT_TYPES } from './constants';

export type RecordData = User | Client | License | Dashboard | StudioAIChatHistoryEntity;

export interface Column {
    name: string;
    value: string;
}

export type AdminToolsParams = Record<string, string | number>;

export interface UpdateResponse {
    hasError: boolean;
    message: string;
    contact?: TeamMemberInvite;
}

export interface VTrackerFormData {
    clientId: string;
    projectIds: number[];
    title: string;
    description: string;
    clientBrandImage: string;
    displayImage: string;
    embedUrl: string;
}

// Report configuration types
export type ReportType = keyof typeof REPORT_TYPES;

export interface ReportConfig {
    data: RecordData[] | undefined;
    isLoading: boolean;
    error: Error | null;
    fetch: (params?: AdminToolsParams, enable?: boolean) => void;
    update: ((id: number, data: Partial<RecordData>) => Promise<UpdateResponse>) | null;
    refetch: () => void;
}

export interface ReportConfigMap {
    [REPORT_TYPES.USERS]: ReportConfig;
    [REPORT_TYPES.CLIENTS]: ReportConfig;
    [REPORT_TYPES.LICENSES]: ReportConfig;
    [REPORT_TYPES.DASHBOARDS]: ReportConfig;
}

export interface User {
    org_user_id: number;
    org_id: number;
    org_name: string;
    user_first_name: string;
    user_last_name: string;
    user_email: string;
    sf_account_id: string;
    sf_account: string;
    sf_parent_id: string;
    sf_parent: string;
    auth_user_external_key: string;
    bi_product_user_external_key: string;
    external_payment_user_id: string;
    has_studio_ai_access: boolean;
    count_in_metrics: boolean;
    last_changed_timestamp: string;
    created_timestamp: string;
    count: string | number;
    [key: string]: string | number | boolean | null | undefined;
}

export interface Client {
    org_id: number;
    org_name: string;
    sf_account_id: string;
    sf_parent_id: string;
    parent_org_name: string;
    contact_first_name: string | null;
    contact_last_name: string | null;
    contact_address_1: string | null;
    contact_address_2: string | null;
    contact_city: string | null;
    contact_state: string | null;
    contact_zip: string | null;
    contact_country: string | null;
    contact_mobile_phone: string | null;
    contact_email: string | null;
    /** Injected client-side from /studio-ai/admin/studio-ai-org-access */
    studio_ai_org_access?: boolean;
    /** Injected client-side: human-friendly limit summary, e.g. "50 messages / 30 days" or "Unlimited" */
    studio_ai_org_access_limit?: string;
    sigma_connection_id: string;
    last_changed_timestamp: string;
    created_timestamp: string;
    count: string | number;
    [key: string]: string | number | boolean | null | undefined;
}

export interface License {
    org_user_id: number;
    org_id: number;
    org_name: string;
    user_first_name: string;
    user_last_name: string;
    user_email: string;
    access_start_date: string;
    access_end_date: string;
    sf_opportunity_id: string | null;
    plan_name: string;
    count: string | number;
    [key: string]: string | number | boolean | null | undefined;
}

export interface Dashboard {
    template_id: number;
    template_name: string;
    template_code: string;
    template_url: string;
    template_desc: string;
    template_icon: string;
    display_order: number;
    is_active: boolean;
    is_vtracker: boolean;
    template_special_title: string;
    project_ids: number[];
    count: string | number;
}

interface OpportunityContact {
    id: string;
    name: string;
    email?: string;
    role?: string;
}

export interface Opportunity {
    amount: number;
    contacts: OpportunityContact[];
    startDate: string;
    endDate: string;
    stage: string;
    planName: string;
    numberOfSeats: number;
}

export interface SubmitFundsResponse {
    hasError: boolean;
    message: string;
    depositOpportunityUrl?: string;
    promoOpportunityUrl?: string;
}

export interface StudioAIChatHistoryEntity {
    id: number;
    conversationId: string;
    orgUserId: number;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    prompt: string;
    title: string;
    threadId?: string;
    lastChangedTimestamp: string;
    messageCount: number;
    feedbackStatus: 'positive' | 'negative' | 'none';
    positiveFeedbackCount: number;
    negativeFeedbackCount: number;
    feedbackWithResponseCount: number;
    feedbackWithoutResponseCount: number;
    hasReport?: boolean;
}

export interface StudioAIConversationReport {
    id: number;
    runId: string;
    title?: string;
    htmlContent: string;
    createdAt: string;
}

export interface StudioAIFeedbackEntity {
    id: number;
    studioaiHistoryId: number;
    threadId: string;
    messageId: string;
    isPositive: boolean;
    feedbackMessage?: string;
    response?: string;
    category?: string;
    createdTimestamp: string;
    lastChangedTimestamp: string;
}

export interface OrgActivationDetailDto {
    orgId: number;
    orgName: string;
    isActivated: boolean;
    firstOnboardDate: Date | null;
    firstActivityDate: Date | null;
}

export interface DailyMetricPointDto {
    date: string;
    value: number;
}

export interface DailyOrgActivityPointDto {
    date: string;
    value: number;
    orgNames: string[];
}

export interface UserActivityMetricsDto {
    totalActiveUsers: number;
    usersWithStudioAI: number;
    activeStudioAIUsers: number;
    activeUserRate: number;
    orgActivationRate: number;
    avgActiveUsersPerOrg: number;
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    monthlyActiveUsers: number;
    orgActivationDetails: OrgActivationDetailDto[];
    dailyOrgActivityTrend: DailyOrgActivityPointDto[];
}

export interface MessageMetricsDto {
    totalUserMessages: number;
    totalAssistantMessages: number;
    totalMessages: number;
    averageMessagesPerConversation: number;
    averageUserMessagesPerUser: number;
    averageAssistantMessagesPerUser: number;
}

export interface ConversationMetricsDto {
    totalConversations: number;
    newConversations: number;
    averageConversationsPerUser: number;
    deletedConversations: number;
    avgSessionsPerUserPerWeek: number;
    avgMessagesPerNewConversation: number;
    dailyConversationTrend: DailyMetricPointDto[];
}

export interface FeedbackMetricsDto {
    totalFeedback: number;
    positiveFeedback: number;
    negativeFeedback: number;
    noFeedback: number;
    noFeedbackRate: number;
    positiveFeedbackRate: number;
    negativeFeedbackRate: number;
    feedbackWithResponse: number;
    feedbackWithoutResponse: number;
    responseRate: number;
    negativeFeedbackNeedingResponse: number;
}

export interface UserUsageDetailDto {
    orgUserId: number;
    userFirstName: string;
    userLastName: string;
    userEmail: string;
    lastUseUtc: Date | null;
    usedWithinAWeek: string;
    usedLast2Week: string;
    usedWithinAMonth: string;
}

export interface UserUsageDetailsDto {
    users: UserUsageDetailDto[];
}

export interface FeedbackDetailDto {
    id: number;
    userEmail: string;
    userFirstName: string | null;
    userLastName: string | null;
    isPositive: boolean;
    feedbackMessage: string | null;
    category: string | null;
    response: string | null;
    isRead: boolean;
    conversationId: string;
    initialQuestion: string | null;
    threadId: string;
    messageId: string;
    createdTimestamp: Date;
    lastChangedTimestamp: Date;
}

export interface FeedbackDetailsDto {
    feedbackRecords: FeedbackDetailDto[];
}

export enum MetricsTimePeriod {
    TODAY = 'today',
    ONE_WEEK = '1week',
    TWO_WEEKS = '2weeks',
    ONE_MONTH = '1month',
    TWO_MONTHS = '2months',
    SIX_MONTHS = '6months',
    ONE_YEAR = '1year',
    ALL = 'all',
}

export enum MetricsUserType {
    ALL = 'all',
    INTERNAL = 'internal',
    FULL_USERS = 'full_users',
    FREEMIUM = 'freemium',
    TRIAL = 'trial',
}

export interface OrgTokenUsageDto {
    orgId: number;
    orgName: string;
    inputTokens: number;
    inputTokensUncached: number;
    outputTokens: number;
    outputTokensUncached: number;
    totalTokens: number;
    activeUsers: number;
    totalConversations: number;
    totalValuableConversations: number;
}

export interface TokenUsageMetricsDto {
    totalInputTokens: number;
    totalInputTokensUncached: number;
    totalOutputTokens: number;
    totalOutputTokensUncached: number;
    totalTokens: number;
    totalConversations: number;
    totalValuableConversations: number;
    usageByOrg: OrgTokenUsageDto[];
}

// Day of Week Activity DTO
export interface DayOfWeekActivityDto {
    dayOfWeek: number;
    dayName: string;
    conversationCount: number;
    messageCount: number;
    uniqueUsers: number;
    percentageOfTotal: number;
}

// Days Active Metrics DTO
export interface DaysActiveMetricsDto {
    avgDaysActivePerUser: number;
    totalActiveDays: number;
    maxDaysActive: number;
    minDaysActive: number;
    distribution: Record<string, number>;
}

// User Engagement DTO (individual user metrics)
export interface UserEngagementDto {
    orgUserId: number | null;
    freemiumUserId: string | null;
    userFirstName: string;
    userLastName: string;
    userEmail: string;
    daysActive: number;
    conversationCount: number;
    messageCount: number;
    avgMessagesPerConversation: number;
    lastActivityAt: Date | null;
    firstActivityAt: Date | null;
    positiveFeedbackCount: number;
    negativeFeedbackCount: number;
    dayOfWeekActivity: DayOfWeekActivityDto[];
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
}

// Engagement Metrics DTO (flat structure - filtering done via userType parameter)
export interface EngagementMetricsDto {
    daysActive: DaysActiveMetricsDto;
    dayOfWeekActivity: DayOfWeekActivityDto[];
    userEngagement: UserEngagementDto[];
    totalUsersAnalyzed: number;
}

// =============================================================================
// Cortex Usage Metrics DTOs (Snowflake account_usage views)
// =============================================================================

// User-level Cortex usage
export interface CortexUserUsageDto {
    username: string;
    userEmail: string | null;
    userFirstName: string | null;
    userLastName: string | null;
    totalTokens: number;
    totalCredits: number;
    queryCount: number;
    avgTokensPerQuery: number;
    firstActivity: Date | null;
    lastActivity: Date | null;
}

// Daily usage trend data
export interface CortexDailyTrendDto {
    date: string;
    tokens: number;
    credits: number;
    queries: number;
}

// Aggregated Cortex usage metrics
export interface CortexUsageMetricsDto {
    totalTokens: number;
    totalCredits: number;
    totalQueries: number;
    uniqueUsers: number;
    avgTokensPerQuery: number;
    avgCreditsPerUser: number;
    analystCredits: number;
    aiSqlCredits: number;
    searchCredits: number;
    userUsage: CortexUserUsageDto[];
    dailyTrend: CortexDailyTrendDto[];
}

// Known data modalities (tables) that can be queried
export enum DataModality {
    APP = 'APP',
    PURCHASE = 'PURCHASE',
    VENUE = 'VENUE',
    SURVEY = 'SURVEY',
    DEMOGRAPHICS = 'DEMOGRAPHICS',
    WEB = 'WEB',
}

// SQL query extracted from conversation
export interface SqlQueryDto {
    sql: string;
    tables: string[];
    modalities: DataModality[];
    timestamp: Date;
}

// Modality usage per conversation
export interface ConversationModalityDto {
    conversationId: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    orgId: number;
    orgName: string;
    modalitiesUsed: DataModality[];
    tablesQueried: string[];
    sqlQueries: SqlQueryDto[];
    queryCount: number;
    timestamp: Date;
}

// Modality usage statistics per user
export interface UserModalityUsageDto {
    orgUserId: number;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    modalityUsage: Record<DataModality, number>;
    totalQueries: number;
    conversationsWithQueries: number;
}

// Modality usage statistics per organization
export interface OrgModalityUsageDto {
    orgId: number;
    orgName: string;
    modalityUsage: Record<DataModality, number>;
    totalQueries: number;
    usersWithQueries: number;
    conversationsWithQueries: number;
}

// Filter option for organization dropdown
export interface OrgFilterOptionDto {
    orgId: number;
    orgName: string;
    userCount: number;
}

// Filter option for user dropdown
export interface UserFilterOptionDto {
    orgUserId: number;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    orgId: number;
    orgName: string;
}

// Aggregate modality metrics
export interface ModalityMetricsDto {
    totalQueries: number;
    conversationsWithQueries: number;
    usersWithQueries: number;
    aggregateModalityUsage: Record<DataModality, number>;
    modalityPercentages: Record<DataModality, number>;
    usageByOrg: OrgModalityUsageDto[];
    allUsers: UserModalityUsageDto[];
    topUsersByQueries: UserModalityUsageDto[];
    recentConversations: ConversationModalityDto[];
    availableOrgs: OrgFilterOptionDto[];
    availableUsers: UserFilterOptionDto[];
}

export interface StudioAIMetricsEntity {
    timePeriod: MetricsTimePeriod;
    startDate: Date | null;
    endDate: Date;
    userActivity: UserActivityMetricsDto;
    messages: MessageMetricsDto;
    conversations: ConversationMetricsDto;
    feedback: FeedbackMetricsDto;
    userUsageDetails: UserUsageDetailsDto;
    feedbackDetails: FeedbackDetailsDto;
    tokenUsage: TokenUsageMetricsDto;
    engagementMetrics: EngagementMetricsDto;
    cortexUsage: CortexUsageMetricsDto | null;
    modalityMetrics: ModalityMetricsDto;
}

// Message Marker Status Enum
export enum MessageMarkerStatus {
    SIGNIFICANT = 'significant',
    VALUABLE_TO_CLIENT = 'valuable_to_client',
    CONCERN = 'concern',
    TRAINING_EXAMPLE = 'training_example',
    FOLLOW_UP_NEEDED = 'follow_up_needed',
}

// Message Marker Entity (from API)
export interface MessageMarkerEntity {
    id: number;
    conversationId: string;
    messageId: string;
    status: MessageMarkerStatus;
    notes?: string;
    createdBy: string;
    userMessage?: string;
    aiResponse?: string;
    createdTimestamp: Date;
    lastChangedTimestamp: Date;
    // Additional conversation context
    userEmail?: string;
    userFirstName?: string;
    userLastName?: string;
    conversationTitle?: string;
}

export interface StudioAIUserAccessEntity {
    id: number;
    orgUserId: number;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    isEnabled: boolean;
    countInMetrics: boolean;
    accessGrantedTimestamp: Date;
    accessRevokedTimestamp?: Date;
    grantedByEmail?: string;
    notes?: string;
    createdTimestamp: Date;
    lastChangedTimestamp: Date;
}

export type StudioAIOrgAccessLimitType = 'message' | 'token';

export const STUDIO_AI_ORG_ACCESS_LIMIT_TYPES: StudioAIOrgAccessLimitType[] = ['message', 'token'];

export type StudioAIOrgAccessType = 'license' | 'on_demand' | 'trial' | 'unlimited';

export const STUDIO_AI_ORG_ACCESS_TYPES: StudioAIOrgAccessType[] = [
    'license',
    'on_demand',
    'trial',
    'unlimited',
];

export const STUDIO_AI_ORG_ACCESS_TYPE_LABELS: Record<StudioAIOrgAccessType, string> = {
    license: 'Subscription',
    on_demand: 'Credit pack',
    trial: 'Trial',
    unlimited: 'Unlimited',
};

export interface StudioAIOrgAccessUsage {
    currentCount: number;
    hasReachedLimit: boolean;
    daysUntilReset: number | null;
    limitMessage?: string | null;
}

export interface StudioAIOrgAccessEntity {
    id: number;
    orgId: number;
    orgName?: string;
    sfAccountId?: string;
    isEnabled: boolean;
    accessType?: StudioAIOrgAccessType | null;
    limitType?: StudioAIOrgAccessLimitType | null;
    limitValue?: number | null;
    windowDays?: number | null;
    accessGrantedTimestamp: string | Date;
    accessRevokedTimestamp?: string | Date;
    grantedByEmail?: string;
    notes?: string;
    trialStartDate?: string | Date | null;
    trialEndDate?: string | Date | null;
    createdTimestamp: string | Date;
    lastChangedTimestamp: string | Date;
    usage?: StudioAIOrgAccessUsage;
}

export interface AddStudioAIOrgAccessPayload {
    orgId: number;
    accessType?: StudioAIOrgAccessType | null;
    limitType?: StudioAIOrgAccessLimitType | null;
    limitValue?: number | null;
    windowDays?: number | null;
    trialStartDate?: string | null;
    trialEndDate?: string | null;
    notes?: string;
}

export interface UpdateStudioAIOrgAccessPayload {
    isEnabled: boolean;
    accessType?: StudioAIOrgAccessType | null;
    limitType?: StudioAIOrgAccessLimitType | null;
    limitValue?: number | null;
    windowDays?: number | null;
    trialStartDate?: string | null;
    trialEndDate?: string | null;
    notes?: string;
}

export interface StudioAIOrgAccessTypeCounts {
    license: number;
    on_demand: number;
    trial: number;
    unlimited: number;
    unset: number;
    total: number;
}

export interface SalesforceCreditPreview {
    orgId: number;
    sfAccountId: string;
    accessType: 'license' | 'on_demand' | null;
    planTier?: string | null;
    annualContractCredits: number;
    creditPackCredits: number;
    creditPackCount: number;
    monthlyCap: number;
    contractStartDate?: string | null;
    contractEndDate?: string | null;
    fetchedAt: string;
}

// DANI Config Types
export type DaniConfigType = 'standard' | 'freemium';

export const DANI_CONFIG_TYPES: DaniConfigType[] = ['standard', 'freemium'];

export interface DaniConfigRevisionEntity {
    id: number;
    type: DaniConfigType;
    account: string;
    database: string;
    schema: string;
    agentName: string;
    infoCautionToolNames?: string[];
    internalOnly: boolean;
    createdAt: string;
    createdBy: string;
}

export interface DaniConfigShape {
    account: string;
    database: string;
    schema: string;
    agentName: string;
    infoCautionToolNames?: string[];
}

export interface DaniConfigActiveState {
    configExternal: DaniConfigShape | null;
    configInternal: DaniConfigShape | null;
    activeRevisionId: number | null;
    activeInternalRevisionId: number | null;
}

export interface DaniConfigRevisionCreatePayload {
    type: DaniConfigType;
    account: string;
    database: string;
    schema: string;
    agentName: string;
    infoCautionToolNames?: string[];
    internalOnly?: boolean;
}

export interface DaniConfigRevisionUpdatePayload {
    account?: string;
    database?: string;
    schema?: string;
    agentName?: string;
    infoCautionToolNames?: string[];
    internalOnly?: boolean;
}

export interface DaniConfigSetActivePayload {
    type: DaniConfigType;
    revisionId: string;
    isInternal: boolean;
}

export interface DaniConfigTestConnectionPayload {
    account: string;
    database: string;
    schema: string;
    agentName: string;
    salesforceOrgId?: string;
}

export interface DaniConfigOverrideEntry {
    id: number;
    orgUserId?: number;
    userEmail?: string | null;
    orgId?: number;
    orgName?: string | null;
    revisionId: number;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
    revision: DaniConfigRevisionEntity | null;
}

export interface DaniConfigSetOverridePayload {
    revisionId: number;
}

export interface ReleaseNoteEntity {
    id: number;
    version: string;
    title: string;
    body: string;
    publishedAt: string | null;
    isActive: boolean;
    audienceAll: boolean;
    audienceInternalOnly: boolean;
    status: ReleaseNoteStatus;
    createdTimestamp: string;
    lastChangedTimestamp: string;
    createdBy: string;
    updatedBy: string | null;
    contentUpdatedAt: string | null;
    releaseName: string | null;
    productionDate: string | null;
    featureAreas: string[];
    videoUrl: string | null;
    imageUrls: Record<string, string>;
}

export type ReleaseNoteStatus = 'draft' | 'published' | 'scheduled' | 'inactive';

/** Target audience for a release note */
export type Audience = 'all' | 'internal';

/** Local form state for creating or editing a release note */
export interface ReleaseNoteFormData {
    releaseName: string;
    productionDate: string;
    title: string;
    body: string;
    featureAreas: string[];
    videoUrl: string;
    publishedAt: string;
    imageUrls: Record<string, string>;
}

export interface CreateReleaseNoteDto {
    releaseName: string;
    productionDate: string;
    title: string;
    body: string;
    featureAreas: string[];
    videoUrl?: string | null;
    publishedAt?: string;
}

export interface UpdateReleaseNoteDto {
    releaseName?: string;
    productionDate?: string | null;
    title?: string;
    body?: string;
    featureAreas?: string[];
    videoUrl?: string | null;
    publishedAt?: string | null;
}

export interface FilterOption {
    name: string;
    value: string;
    type: 'boolean';
}

export interface ActiveFilter {
    field: string;
    fieldName: string;
    value: string;
    displayValue: string;
}

export interface FreemiumIpUsage {
    ip: string;
    count: number;
    isBlocked?: boolean; // Not in response yet, but needed for UI state
}

export interface FreemiumUsageResponse {
    window: string;
    ips: FreemiumIpUsage[];
}

export interface BlockedIp {
    id: string;
    ip: string;
    isBlocked: boolean;
    expiresAt: string | null;
    reason: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
}

export interface BlockedIpsResponse {
    data: BlockedIp[];
    total: number;
    page: number;
    limit: number;
}
